import { IonButton } from '@ionic/react';
import { MouseEventHandler, ReactNode, useEffect, useState } from 'react';
import { Color, Size, Fill } from '../common/index.ts';
import { ButtonSpinner } from '../spinner/ButtonSpinner.tsx';
import { useBreakpoints } from '../../support/hooks/useBreakpoints.tsx';
import { NewFeatureBadge } from '../badge/NewFeatureBadge.tsx';

export interface ButtonProps {
    id?: string;
    color?: Color;
    size?: Size;
    fill?: Fill;
    minWidth?: number;
    expand?: 'block';
    disabled?: boolean;
    href?: string;
    type?: 'button' | 'reset' | 'submit';
    dataType?: string;
    dataSet?: {[k: string]: string};
    onClick?: MouseEventHandler;
    newFeatureKey?: string;
    children: ReactNode;
}

export const Button: React.FC<ButtonProps> = ({ id, color = 'primary', size = 'medium', minWidth, fill, disabled = false, expand, onClick, href, type = 'button', newFeatureKey, dataType, dataSet = {}, children }) => {
    const { isAboveBreakpoint } = useBreakpoints();

    const [mounted, setMounted] = useState(false);
    const [working, setWorking] = useState(false);

    // limit full-width buttons on wider screens so they don't look ridiculous
    let textAlign: 'center' | undefined;
    if (expand && minWidth === undefined && isAboveBreakpoint('md')) {
        expand = undefined;
        minWidth = 300;
        textAlign = 'center';
    }

    // style wrapper so it matches size of button, providing an anchor for "new" badge
    const wrapperDisplay = (expand ? 'block' : 'inline-block');
    const wrapperPadding = (expand ? 0 : 0.5);

    // buttons smaller than this are not pleasing to the eye
    if (minWidth === undefined) {
        minWidth = 120;
    }

    const ionSize = (size === 'small' ? 'small' : size === 'large' ? 'large' : 'default');
    const padding = (size === 'large' ? '10px' : undefined);

    const buttonStyle = {
        minWidth,
        opacity: (disabled || working ? 0.3 : 'inherit'),
        whiteSpace: 'nowrap',
        '--padding-top': padding,
        '--padding-bottom': padding
    };

    // support dataType for backward compatibility
    if (dataType) {
        dataSet.type = dataType;
    }

    // build data attributes, e.g. dataSet={id:'test'} -> data-id="test"
    const dataAttributes = Object.keys(dataSet).reduce((map, k) => ({...map, [`data-${k}`]: dataSet[k]}), {});

    // note: for internal routes, don't set href on IonButton, it causes very weird drawer behavior where sometimes it
    // acts like it's open, but renders like it's not, and becomes generally unresponsive
    let routerLink: string | undefined;
    if (href && !href.startsWith('http')) {
        routerLink = href;
        href = undefined;
        if (routerLink && routerLink.startsWith('#')) {
            routerLink = routerLink.substr(1);
        }
    }

    useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    const handleClick = async (event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => {
        if (onClick && !working) {
            setWorking(true);

            await onClick(event);

            // the onClick handler might have unmounted the page, so check we're still mounted
            // before updating state to avoid a react warning in the console
            if (mounted) {
                setWorking(false);
            }
        }
    };

    return (
        <div style={{position: 'relative', borderRadius: 10, border: 0, padding: 0, margin: 0, textAlign}}>
            <div style={{display: wrapperDisplay, padding: wrapperPadding, position: 'relative', borderRadius: 10}}>
                {working &&
                    <ButtonSpinner color="secondary" />
                }
                <IonButton
                    id={id}
                    expand={expand}
                    style={buttonStyle}
                    onClick={handleClick}
                    color={color}
                    size={ionSize}
                    fill={fill}
                    data-href={href}
                    disabled={disabled || working}
                    routerLink={routerLink}
                    routerDirection="none"
                    href={href}
                    type={type}
                    {...dataAttributes}
                >
                    {children}
                </IonButton>
                {newFeatureKey && (
                    <div style={{ position: 'absolute', top: -10, right: -8 }}>
                        <NewFeatureBadge hasBorder featureKey={newFeatureKey} disabled={disabled} />
                    </div>
                )}
            </div>
        </div>
    );
};
